/**
 * Plan catalog contract + seed values (ARCHITECTURE §2).
 *
 * IMPORTANT: these are *seed* values. At runtime the source of truth is the `plans` /
 * `topup_bundles` tables (editable from Super Admin). Business logic must read the DB,
 * never these constants (except seeding and tests).
 */

export const RESOURCES = ['sms', 'email', 'ai'] as const;
export type Resource = (typeof RESOURCES)[number];

export const PLAN_KEYS = ['basic', 'pro'] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];

export const FEATURE_KEYS = [
  'contacts.core',
  'reviews.requests',
  'messages.freetext',
  'schema.custom',
  'segments',
  'automations',
  'ai.assistant',
  'ai.onboarding',
  'ai.copy',
  'insights',
  'dashboard.codegen',
  'google.business',
  /** Forms engine (FORMS_DESIGN §2): announcement + interest declarations. */
  'forms.interest',
  /** Forms engine: events + attendance declarations (RSVP). */
  'forms.events',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

export function isFeatureKey(v: string): v is FeatureKey {
  return (FEATURE_KEYS as readonly string[]).includes(v);
}

/**
 * Features that exist in code but are switched off platform-wide unless the deployment turns them
 * on (ADR-G7). While off they are hidden everywhere (no route, no nav, no upsell, not in plan
 * payloads), even for plans that list them; the plan catalog keeps the key so turning the switch
 * on needs no data change. `google.business` ← API env `GOOGLE_BUSINESS_ENABLED`.
 */
export const PLATFORM_SWITCHED_FEATURES = [
  'google.business',
] as const satisfies readonly FeatureKey[];
export type PlatformSwitchedFeature = (typeof PLATFORM_SWITCHED_FEATURES)[number];

export function isPlatformSwitchedFeature(v: string): v is PlatformSwitchedFeature {
  return (PLATFORM_SWITCHED_FEATURES as readonly string[]).includes(v);
}

/**
 * Whether `key` is available on this deployment: always for ordinary features, and for a switched
 * feature only when it is in `enabled` (the server's `platformFeatures` list). Off by default.
 */
export function isFeatureOnPlatform(key: FeatureKey, enabled: readonly string[]): boolean {
  return !isPlatformSwitchedFeature(key) || enabled.includes(key);
}

export type Allowances = Record<Resource, number>;

export interface PlanLimits {
  /** Number of stores/locations. */
  stores: number;
  /** Team seats (memberships). */
  seats: number;
  /** Soft cap on active contacts (null = unlimited). */
  contacts: number | null;
  /** Max custom field definitions (Pro). */
  customFields: number;
  /** Max active automations (Pro). */
  automations: number;
}

export interface PlanSeed {
  key: PlanKey;
  name: string;
  priceCents: number;
  currency: 'EUR';
  interval: 'month';
  allowances: Allowances;
  features: FeatureKey[];
  limits: PlanLimits;
  sortOrder: number;
}

export const BASIC_FEATURES: FeatureKey[] = [
  'contacts.core',
  'reviews.requests',
  'messages.freetext',
];
export const PRO_FEATURES: FeatureKey[] = [...FEATURE_KEYS];

export const SEED_PLANS: readonly PlanSeed[] = [
  {
    key: 'basic',
    name: 'Basic',
    priceCents: 1900,
    currency: 'EUR',
    interval: 'month',
    allowances: { sms: 50, email: 300, ai: 0 },
    features: BASIC_FEATURES,
    limits: { stores: 1, seats: 2, contacts: 2500, customFields: 0, automations: 0 },
    sortOrder: 1,
  },
  {
    key: 'pro',
    name: 'Pro',
    priceCents: 3900,
    currency: 'EUR',
    interval: 'month',
    allowances: { sms: 100, email: 600, ai: 500 },
    features: PRO_FEATURES,
    limits: { stores: 1, seats: 5, contacts: 10000, customFields: 100, automations: 20 },
    sortOrder: 2,
  },
] as const;

export interface TopupBundleSeed {
  key: string;
  name: string;
  resource: Resource;
  quantity: number;
  priceCents: number;
  currency: 'EUR';
  /** Plans allowed to purchase this bundle (AI top-ups only on Pro). */
  availableOnPlans: PlanKey[];
  sortOrder: number;
}

export const SEED_TOPUP_BUNDLES: readonly TopupBundleSeed[] = [
  {
    key: 'sms_100',
    name: '100 SMS',
    resource: 'sms',
    quantity: 100,
    priceCents: 500,
    currency: 'EUR',
    availableOnPlans: ['basic', 'pro'],
    sortOrder: 1,
  },
  {
    key: 'sms_500',
    name: '500 SMS',
    resource: 'sms',
    quantity: 500,
    priceCents: 2000,
    currency: 'EUR',
    availableOnPlans: ['basic', 'pro'],
    sortOrder: 2,
  },
  {
    key: 'email_1000',
    name: '1.000 email',
    resource: 'email',
    quantity: 1000,
    priceCents: 500,
    currency: 'EUR',
    availableOnPlans: ['basic', 'pro'],
    sortOrder: 3,
  },
  {
    key: 'ai_500',
    name: '500 μονάδες AI',
    resource: 'ai',
    quantity: 500,
    priceCents: 1000,
    currency: 'EUR',
    availableOnPlans: ['pro'],
    sortOrder: 4,
  },
] as const;

/** Convenience for tests/seed only. */
export function seedPlan(key: PlanKey): PlanSeed {
  const p = SEED_PLANS.find((x) => x.key === key);
  if (!p) throw new Error(`unknown plan ${key}`);
  return p;
}

// ─── VAT (ΦΠΑ) ───────────────────────────────────────────────────────────────

/**
 * Catalog prices (plans and top-up bundles) are NET of VAT (ADR-B10): 19 € + ΦΠΑ. VAT is added at
 * checkout with one platform-wide rate, stored in basis points (`platform_config`
 * `billing.vatRateBps`, editable in Super Admin). 2400 = 24%, the standard Greek rate.
 * Allowances never depend on VAT.
 */
export const DEFAULT_VAT_RATE_BPS = 2400;
/** Upper bound accepted from Super Admin (50%): guards against typing 2400 as a percent. */
export const MAX_VAT_RATE_BPS = 5000;

/** VAT on a net amount, rounded half-up to the cent (integer math; 1900 at 2400 bps → 456). */
export function vatCents(netCents: number, rateBps: number): number {
  if (!Number.isInteger(netCents) || !Number.isInteger(rateBps) || rateBps < 0)
    throw new Error(`vatCents: integer inputs required (got ${netCents}, ${rateBps})`);
  const sign = netCents < 0 ? -1 : 1;
  return sign * Math.floor((Math.abs(netCents) * rateBps + 5_000) / 10_000);
}

/** Net + VAT: what the customer pays (1900 → 2356, 3900 → 4836 at 24%). */
export function grossCents(netCents: number, rateBps: number): number {
  return netCents + vatCents(netCents, rateBps);
}

export interface VatBreakdown {
  netCents: number;
  vatCents: number;
  grossCents: number;
  rateBps: number;
}

export function vatBreakdown(netCents: number, rateBps: number): VatBreakdown {
  const vat = vatCents(netCents, rateBps);
  return { netCents, vatCents: vat, grossCents: netCents + vat, rateBps };
}

/** "24", "13,5" style number for a rate in basis points (no % sign, caller formats). */
export function vatRatePercent(rateBps: number): number {
  return rateBps / 100;
}
